import React, { useState } from "react";
import { useReveal } from "../components/Layout";
import { workshopsPreview, workshopVideos } from "../mock";
import { Plus, Minus, Play, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";

const sections = [
  {
    id: "01",
    title: "Kursy animacji i scenografii filmowej",
    body: [
      "Proponowane kursy prowadzone są regularnie, cotygodniowo po 3–4 godziny zegarowe, w małych grupach. Nasze mini studio filmowe wyposażone jest w profesjonalny sprzęt oraz stanowiska do animacji rysunkowej, wycinankowej oraz lalkowej.",
      "Uczestnicy kursu mają okazję korzystać z pracowni, gdzie zrealizują ćwiczenia związane z budową scenografii, makiet oraz wykonają lalki i rekwizyty filmowe.",
      "Program A — dla osób chcących zdawać na uczelnie artystyczne i filmowe. Wprowadzenie do historii animacji, techniki animacji, realizacja filmu od scenariusza po montaż i udźwiękowienie.",
      "Program B — dla osób chcących rozwijać pasje związane z filmem i scenografią. Wszystkie etapy realizacji filmu animowanego z naciskiem na ćwiczenia praktyczne.",
      "Oferujemy także kursy indywidualne oraz współpracę z ośrodkami kultury i innymi instytucjami kulturalnymi.",
    ],
    image: "https://animuszstudio.com/files/2003100523312w1.jpg",
  },
  {
    id: "02",
    title: "Program kursów animacji i scenografii filmowej",
    list: [
      "Wstęp do historii animacji filmowej (projekcje i analiza filmu animowanego)",
      "Plastyka filmowa — techniki plastyczne wykorzystywane w filmie",
      "Zapoznanie z głównymi technikami realizacji (rysunek, wycinanka, lalka, materiały sypkie, piksilacja, technika kombinowana, non camera, efekty specjalne, animacja komputerowa)",
      "Zaprojektowanie postaci i scenografii rysunkowej",
      "Zaprojektowanie i wykonanie lalki oraz scenografii do filmu lalkowego",
      "Zaprojektowanie scenografii oraz postaci do filmu w technice wycinanki",
      "Ekodesign — wykorzystanie odpadów i śmieci do tworzenia scenografii",
      "Ćwiczenia filmowe rozwijające i sprawdzające umiejętności kreacyjne",
      "Pisanie scenariuszy filmowych i realizacja storyboardów",
      "Realizacja animatiku do filmu",
      "Przygotowanie planu zdjęciowego do poszczególnych technik animacji",
      "Ćwiczenia animacyjne w technice rysunku, lalkowej i wycinankowej oraz pikselacji",
      "Efekty specjalne w filmie (bluebox, greenbox, non camera, film z udziałem aktora)",
      "Realizacja własnej etiudy animowanej",
      "Wstęp do animacji komputerowej 2D i podstawowe oprogramowanie",
    ],
    video: workshopVideos[0],
  },
  {
    id: "03",
    title: "Warsztaty animacji i scenografii filmowej",
    body: [
      "Warsztaty prowadzone są cyklicznie, w dwudniowych lub trzydniowych blokach. Skierowane są do szkół, grup zorganizowanych oraz indywidualnych osób chcących w kilka dni zapoznać się z animacją i stworzyć krótki film animowany.",
      "Warsztaty prowadzone są w oparciu o wcześniej przygotowany scenariusz — istnieje możliwość swobodnego doboru tematów (ekologia, aktualne wydarzenia kulturalne itp.). W trakcie trzech dni zrealizujemy krótki film animowany w różnych technikach oraz film dokumentujący działania grupy (making of).",
      "Poprzez działalność Animusz Studio chcemy zarażać pasją do filmu oraz kontynuować tradycje Łodzi jako miasta związanego z kinematografią. Warsztaty i kursy łączą zajęcia filmowe, plastyczne, fotograficzne, teatralne i muzyczne — pobudzają kreatywność, rozwijają wyobraźnię, uczą działania w grupie.",
      "Uczestnicy mają szansę stworzyć ekipę filmową, w której wcielają się w role scenarzysty, scenografa, reżysera, animatora oraz aktora — poznają w praktyce charakter tych zawodów.",
    ],
    video: workshopVideos[1],
  },
];

const Warsztaty = () => {
  useReveal();
  const [open, setOpen] = useState("01");

  return (
    <div className="pt-24">
      {/* HERO */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-8 pb-12">
        <div className="flex justify-between mono text-[11px] tracking-[0.25em] opacity-60 mb-16">
          <div>[ 03 — WARSZTATY ]</div>
          <div>KURSY / WARSZTATY / EDUKACJA</div>
        </div>
        <h1 className="display text-[15vw] md:text-[12vw] leading-[0.85] rise-in">
          Ucz się
          <br />
          <span className="serif italic font-normal">ożywiać</span>
          <br />
          <span className="text-[#FF4D2E]">obrazy.</span>
        </h1>
        <div className="mt-10 md:mt-16 grid md:grid-cols-12 gap-6">
          <p className="md:col-start-6 md:col-span-6 text-lg md:text-xl leading-snug reveal">
            <span className="serif italic">Animusz Studio</span> posiada profesjonalne zaplecze sprzętowe i mini studio filmowe, w którym odbywają się kursy i warsztaty animacji filmowej, warsztaty plastyczne i scenograficzne dla dzieci i dorosłych. Siedziba znajduje się w przestrzeniach <span className="text-[#FF4D2E] font-bold">Fabryki Sztuki w Łodzi</span>.
          </p>
        </div>
      </section>

      {/* WORKSHOP GIFS */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {workshopsPreview.map((w, i) => (
            <div key={w.id} className="pf-card reveal aspect-square overflow-hidden bg-[#E8E2D3] relative group" style={{transitionDelay: `${i * 80}ms`}}>
              <img src={w.gif} alt={w.title} className="w-full h-full object-cover" />
              <div className="absolute bottom-3 left-3 mono text-[10px] tracking-widest bg-[#0E0E0E]/70 text-[#F2EEE5] px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500 backdrop-blur">
                {String(i + 1).padStart(2, "0")} · WARSZTATY
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INTRO PARAGRAPH */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-24">
        <p className="reveal max-w-4xl serif italic text-2xl md:text-3xl leading-snug">
          Proponujemy szeroką ofertę warsztatową dla różnych grup wiekowych. Program zajęć jest ustalany indywidualnie w zależności od wieku, zainteresowań uczestników i zapotrzebowania.
        </p>
      </section>

      {/* ACCORDION */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-24">
        <div className="reveal mb-12">
          <div className="mono text-[11px] tracking-[0.3em] opacity-60 mb-4">[ OFERTA ]</div>
          <h2 className="display text-5xl md:text-7xl">Program.</h2>
        </div>
        <div className="border-t border-black/20">
          {sections.map(sec => {
            const isOpen = open === sec.id;
            return (
              <div key={sec.id} className="border-b border-black/20">
                <button onClick={() => setOpen(isOpen ? null : sec.id)} className="w-full flex items-center gap-6 py-8 md:py-10 text-left group">
                  <span className="mono text-sm opacity-50">+ {sec.id}</span>
                  <span className="display text-2xl md:text-4xl flex-1">{sec.title.toUpperCase()}</span>
                  <span className="w-10 h-10 rounded-full border border-current flex items-center justify-center group-hover:bg-[#FF4D2E] group-hover:border-[#FF4D2E] group-hover:text-[#F2EEE5] transition-all duration-300">
                    {isOpen ? <Minus size={16} /> : <Plus size={16} />}
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-700 ease-out ${isOpen ? "max-h-[3000px] opacity-100" : "max-h-0 opacity-0"}`}>
                  <div className="pb-12 grid md:grid-cols-12 gap-8">
                    <div className="md:col-span-7 space-y-5 text-base md:text-lg leading-relaxed">
                      {sec.body && sec.body.map((p, i) => <p key={i}>{p}</p>)}
                      {sec.list && (
                        <ul className="space-y-3">
                          {sec.list.map((l, i) => (
                            <li key={i} className="flex gap-4 border-b border-black/10 pb-3">
                              <span className="mono text-xs opacity-50 pt-1">{String(i + 1).padStart(2, "0")}</span>
                              <span>{l}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="md:col-span-5">
                      {sec.image && (
                        <div className="aspect-[4/3] overflow-hidden bg-[#E8E2D3] pf-card">
                          <img src={sec.image} alt={sec.title} className="w-full h-full object-cover" />
                        </div>
                      )}
                      {sec.video && (
                        <div className="aspect-video overflow-hidden bg-[#0E0E0E] relative">
                          <iframe title={sec.video.title} src={sec.video.url} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
                        </div>
                      )}
                      {sec.video && (
                        <div className="mt-3 mono text-xs opacity-60">▶ {sec.video.title} — {sec.video.channel}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 max-w-[1800px] mx-auto pt-32">
        <div className="bg-[#FF4D2E] text-[#0E0E0E] p-10 md:p-20 reveal">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="mono text-[11px] tracking-[0.3em] opacity-70 mb-6">[ DOŁĄCZ DO WARSZTATÓW ]</div>
              <h3 className="display text-5xl md:text-8xl leading-[0.9]">
                Odkryj <span className="serif italic">nieznany</span><br />talent.
              </h3>
            </div>
            <div className="md:col-span-4">
              <Link to="/kontakt" className="group inline-flex items-center gap-3 bg-[#0E0E0E] text-[#F2EEE5] px-6 py-4 hover:bg-[#F2EEE5] hover:text-[#0E0E0E] transition-colors duration-300">
                <span className="text-lg">Zapisz się</span>
                <ArrowUpRight size={20} className="group-hover:rotate-45 transition-transform duration-500" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Warsztaty;
